// Quote Builder — header bar.
//
// Verbatim port of source `renderHeader` (kos-qb-v3.js:1385–1438).
// Three regions:
//   • Brand: "QUOTE BUILDER" label with brand-dot
//   • Meta: customer-pill (with optional CRM call/message icons),
//     project pill (if set), reference pill, clickable status badge,
//     "Saved Xs ago" indicator
//   • Actions: Toggle (left panel) · Shortcuts (?) · Quotes (load archive)
//     · Save (to archive) · Clear · Kit-Q · Minimise

import { iconMarkup } from "@tkos/shared-ui";
import type { QuoteStore } from "./state";
import type { QbStatus } from "./types";
import type { PageContext } from "../../shell/types";
import { getCachedUser } from "../../shell/auth";
import { escapeHtml, fmtAgo, ICONS, svgIcon } from "./formatters";

const STATUS_LABELS: Record<QbStatus, { label: string; color: string; bg: string }> = {
  draft:    { label: "Draft",    color: "#6b7280", bg: "rgba(107,114,128,.18)" },
  sent:     { label: "Sent",     color: "#0ea5e9", bg: "rgba(14,165,233,.18)" },
  approved: { label: "Approved", color: "#22c55e", bg: "rgba(34,197,94,.18)" },
  ordered:  { label: "Ordered",  color: "#e58200", bg: "rgba(229,130,0,.18)" },
};

const NEXT_STATUS: Record<QbStatus, QbStatus> = {
  draft: "sent",
  sent: "approved",
  approved: "ordered",
  ordered: "draft",
};

export interface HeaderHandlers {
  onMinimise: () => void;
  onClear: () => void;
  onSave: () => void;
  onClose: () => void;
  /** Jump straight to this customer's CRM record. Distinct from the CRM
   *  quick-action on the customer pill, which lands on the CRM *list* with the
   *  contact highlighted — that was three clicks from the record itself. */
  onOpenCustomerRecord: (customerId: string) => void;
  onKitQ: () => void;
  onToggleLeft: () => void;
  onShowShortcuts: () => void;
  onLoadQuote: () => void;
  onDuplicate: () => void;
}

// Concise "Opened" timestamp, e.g. "24 Jun 14:32".
const SEEN_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
});
function fmtSeenDate(ms: number): string {
  return Number.isFinite(ms) && ms > 0 ? SEEN_FMT.format(new Date(ms)) : "";
}

// ── CRM record lookup (best-effort) ────────────────────────────────────
//
// Source line 1392 reads `window._crmGetAll()` synchronously from the
// CRM module. Modular has no equivalent injected global, so we fetch
// the contacts list async + cache it. When a quote's customer matches a
// CRM record, we surface the phone + message quick-actions.

interface CrmRecord {
  id?: string;
  name?: string;
  phone?: string;
  email?: string;
}

const CRM_CACHE: { records: CrmRecord[] | null; loading: boolean } = {
  records: null, loading: false,
};

async function loadCrm(): Promise<CrmRecord[]> {
  if (CRM_CACHE.records) return CRM_CACHE.records;
  if (CRM_CACHE.loading) return [];
  CRM_CACHE.loading = true;
  try {
    const res = await fetch("/api/portal/customers", {
      credentials: "include",
      headers: { "x-retailer-id": getCachedUser()?.retailerSlug || getCachedUser()?.retailerId || "" },
    });
    if (!res.ok) throw new Error("crm list failed");
    const data = await res.json();
    CRM_CACHE.records = Array.isArray(data.customers) ? data.customers : (Array.isArray(data.contacts) ? data.contacts : []);
  } catch {
    CRM_CACHE.records = [];
  }
  CRM_CACHE.loading = false;
  return CRM_CACHE.records ?? [];
}

function findCrmRecord(name: string): CrmRecord | null {
  if (!name || !CRM_CACHE.records) return null;
  const lc = name.toLowerCase().trim();
  return CRM_CACHE.records.find((c) => String(c.name ?? "").toLowerCase().trim() === lc) ?? null;
}

// ── Render ────────────────────────────────────────────────────────────

export function mountHeader(
  host: HTMLElement,
  store: QuoteStore,
  handlers: HeaderHandlers,
  ctx: PageContext,
): () => void {
  let crmLoaded = false;
  void loadCrm().then(() => { crmLoaded = true; render(); });

  function render(): void {
    const q = store.get();
    const st = STATUS_LABELS[q.status];
    const crmRec = q.customer ? findCrmRecord(q.customer) : null;
    const safePhone = crmRec?.phone ? String(crmRec.phone).replace(/\s/g, "") : "";

    // Customer pill — CRM-linked variant when matched (source 1402–1410)
    const userIcon = svgIcon("M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2|M12 7a4 4 0 1 1 0 8 4 4 0 0 1 0-8");
    const custInner = crmRec
      ? `
        ${userIcon}
        <a class="qb-meta-pill__crm-name" data-role="crm-open" data-crm-name="${escapeHtml(crmRec.name ?? q.customer)}">${escapeHtml(q.customer || "New customer")}</a>
        ${safePhone ? `<a class="qb-meta-pill__crm-act qb-meta-pill__crm-act--call" data-role="crm-call" data-phone="${escapeHtml(safePhone)}" title="Call ${escapeHtml(crmRec.name ?? "customer")}">${svgIcon("M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z")}</a>` : ""}
        <a class="qb-meta-pill__crm-act qb-meta-pill__crm-act--msg" data-role="crm-msg" data-crm-name="${escapeHtml(crmRec.name ?? q.customer)}" title="Message">${svgIcon("M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z")}</a>
      `
      : `${userIcon} <strong>${escapeHtml(q.customer || "New customer")}</strong>`;
    const custPill = `<span class="qb-meta-pill ${crmRec ? "qb-meta-pill--crm" : ""}" title="${crmRec ? "Customer (linked to CRM — click to open record)" : "Customer"}">${custInner}</span>`;

    // Project pill (only if project set)
    const projectPill = q.project
      ? `<span class="qb-meta-pill" title="Project"><strong>${escapeHtml(q.project)}</strong></span>`
      : "";

    // Reference pill
    const fileIcon = svgIcon("M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8|M14 2v6h6");
    const refPill = `<span class="qb-meta-pill" title="Reference">${fileIcon} ${escapeHtml(q.reference)}</span>`;

    // Status badge (clickable)
    const statusBadge = `<button type="button" class="qb-status" data-role="status" style="background:${st.bg};color:${st.color};border-color:${st.color}40" title="Click to advance status">${escapeHtml(st.label)}</button>`;

    // Saved pill
    const savedPill = `<span class="qb-meta-pill qb-saved" data-role="saved" title="Autosaves on every change">Saved ${escapeHtml(fmtAgo(q.lastSavedAt))}</span>`;

    // "Seen" pill — shown once the customer has opened this quote in their
    // portal (server-stamped `openedAt`). Subtle, positive engagement signal;
    // nothing renders before the first open.
    const seenPill = (q.openedAt && q.openedAt > 0)
      ? `<span class="qb-meta-pill qb-meta-pill--seen" title="Customer opened this quote">${svgIcon("M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z|M12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6")} Opened ${escapeHtml(fmtSeenDate(q.openedAt))}</span>`
      : "";

    // Actions row — verbatim source button order (1424–1437)
    const actions = `
      <button type="button" class="qb-btn qb-btn--ghost" data-role="toggle-left" title="Toggle left panel (Ctrl+B)">
        ${svgIcon("M3 6h18|M3 12h18|M3 18h18")} Toggle
      </button>
      <button type="button" class="qb-btn qb-btn--ghost" data-role="shortcuts" title="Keyboard shortcuts (?)">
        ${svgIcon("M4 7h16|M4 12h16|M4 17h10")} Shortcuts
      </button>
      <button type="button" class="qb-btn qb-btn--ghost" data-role="load-quote" title="Load a saved quote">
        ${svgIcon("M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z|M9 3v18|M15 3v18")} Quotes
      </button>
      <button type="button" class="qb-btn qb-btn--ghost" data-role="save" title="Save quote to archive (Ctrl+S)">
        ${svgIcon(ICONS.save)} Save
      </button>
      ${q.customerId ? `
      <button type="button" class="qb-btn qb-btn--ghost" data-role="open-customer" title="Open this customer's CRM record">
        ${svgIcon("M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2|M12 7a4 4 0 1 1 0 8 4 4 0 0 1 0-8")} Customer
      </button>` : ""}
      <button type="button" class="qb-btn qb-btn--ghost" data-role="close" title="Close and return to where you came from">
        ${svgIcon("M18 6L6 18|M6 6l12 12")} Close
      </button>
      <button type="button" class="qb-btn qb-btn--ghost" data-role="duplicate" title="Duplicate this quote as a new draft for the same customer">
        ${svgIcon("M8 8h11a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z|M4 16H3V4a1 1 0 0 1 1-1h12v1")} Duplicate
      </button>
      <button type="button" class="qb-btn qb-btn--ghost qb-btn--danger" data-role="clear" title="Clear quote and start new">
        ${svgIcon("M3 6h18|M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6|M10 11v6|M14 11v6")} Clear
      </button>
      <button type="button" class="qb-btn qb-btn--ghost qb-btn--kitq" data-role="kitq" title="Kit-Q AI assistant">
        ${iconMarkup("sparkle", { size: "sm" })} Kit-Q
      </button>
      <button type="button" class="qb-btn qb-btn--primary" data-role="minimise" title="Save & return to dashboard (Ctrl+M)">
        ${svgIcon("M5 12h14")} Minimise
      </button>
    `;

    host.innerHTML = `
      <header class="qb-head">
        <div class="qb-head__brand">
          <span class="qb-head__brand-dot"></span>
          QUOTE BUILDER
        </div>
        <div class="qb-head__meta">
          ${custPill}
          ${projectPill}
          ${refPill}
          ${statusBadge}
          ${seenPill}
          ${savedPill}
        </div>
        <div class="qb-head__actions">
          ${actions}
        </div>
      </header>
    `;
    wire();
    void crmLoaded;
  }

  function wire(): void {
    host.querySelector<HTMLButtonElement>("[data-role='status']")?.addEventListener("click", () => {
      store.set((d) => { d.status = NEXT_STATUS[d.status]; });
    });
    host.querySelector<HTMLButtonElement>("[data-role='save']")?.addEventListener("click", handlers.onSave);
    host.querySelector<HTMLButtonElement>("[data-role='close']")?.addEventListener("click", handlers.onClose);
    host.querySelector<HTMLButtonElement>("[data-role='open-customer']")?.addEventListener("click", () => {
      const id = (store.get().customerId || "").trim();
      if (id) handlers.onOpenCustomerRecord(id);
    });
    host.querySelector<HTMLButtonElement>("[data-role='duplicate']")?.addEventListener("click", handlers.onDuplicate);
    host.querySelector<HTMLButtonElement>("[data-role='clear']")?.addEventListener("click", handlers.onClear);
    host.querySelector<HTMLButtonElement>("[data-role='kitq']")?.addEventListener("click", handlers.onKitQ);
    host.querySelector<HTMLButtonElement>("[data-role='minimise']")?.addEventListener("click", handlers.onMinimise);
    host.querySelector<HTMLButtonElement>("[data-role='toggle-left']")?.addEventListener("click", handlers.onToggleLeft);
    host.querySelector<HTMLButtonElement>("[data-role='shortcuts']")?.addEventListener("click", handlers.onShowShortcuts);
    host.querySelector<HTMLButtonElement>("[data-role='load-quote']")?.addEventListener("click", handlers.onLoadQuote);

    // CRM quick-actions
    //
    // G7 — open record: navigates to the CRM page focused on this contact.
    //   The CRM page reads `ctx.params["focus"]` after first render and
    //   scrolls + highlights the matching lead (crm/page.ts:1155).
    //
    // G8 — call: STILL A PLAIN `tel:` STUB. Defraud-relevant — the source
    //   path uses `window.kosC2cCall` which routes through Twilio call
    //   masking so the customer never sees the retailer's personal mobile
    //   and the call is platform-logged. Wiring this requires the Twilio
    //   click-to-call endpoint and is held until you explicitly approve.
    //
    // G9 — message: navigates to the Messages page focused on this
    //   contact. The Messages page reads `ctx.params["contact"]` and
    //   selects the matching thread after threads load. Platform-only
    //   messaging per [[project-platform-messaging-only]].
    host.querySelector<HTMLElement>("[data-role='crm-open']")?.addEventListener("click", (e) => {
      e.preventDefault();
      // When the quote carries a real customer id, go straight to that
      // customer's record. The CRM-list-with-focus route below stays as the
      // fallback for a quote whose customer is still only free text.
      const linkedId = (store.get().customerId || "").trim();
      if (linkedId) { handlers.onOpenCustomerRecord(linkedId); return; }
      const name = (e.currentTarget as HTMLElement).dataset["crmName"];
      if (!name) return;
      const rec = findCrmRecord(name);
      const focus = rec?.id ?? name;
      ctx.navigate("crm", { focus });
    });
    host.querySelector<HTMLElement>("[data-role='crm-call']")?.addEventListener("click", (e) => {
      e.preventDefault();
      const phone = (e.currentTarget as HTMLElement).dataset["phone"];
      // G8 is held — `tel:` is the safe fallback for now. Replacing this
      // with the Twilio click-to-call endpoint touches defraud invariants
      // and needs explicit Steve sign-off.
      if (phone) window.location.href = `tel:${phone}`;
    });
    host.querySelector<HTMLElement>("[data-role='crm-msg']")?.addEventListener("click", (e) => {
      e.preventDefault();
      const name = (e.currentTarget as HTMLElement).dataset["crmName"];
      if (!name) return;
      ctx.navigate("messages", { contact: name });
    });
  }

  render();
  const unsub = store.subscribe(() => {
    if (document.activeElement && host.contains(document.activeElement)) return;
    render();
  });

  // Tick the "Saved Xs ago" pill every 10 s.
  const interval = window.setInterval(() => {
    const savedEl = host.querySelector<HTMLElement>("[data-role='saved']");
    if (savedEl) savedEl.textContent = `Saved ${fmtAgo(store.get().lastSavedAt)}`;
  }, 10_000);

  return () => {
    unsub();
    window.clearInterval(interval);
  };
}
